import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Award, ScanLine, RefreshCw, FileSpreadsheet, MapPin, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { UFS } from "@/lib/ufs";

// Chaves de persistência dos filtros (o operador volta e mantém a escolha)
const STATE_FILTER_KEY = "ceap_eventos_uf_filter";
const MONTH_FILTER_KEY = "ceap_eventos_month_filter";
const YEAR_FILTER_KEY = "ceap_eventos_year_filter";

const MONTHS = [
  { value: "1", label: "Janeiro" },   { value: "2", label: "Fevereiro" }, { value: "3", label: "Março" },
  { value: "4", label: "Abril" },     { value: "5", label: "Maio" },      { value: "6", label: "Junho" },
  { value: "7", label: "Julho" },     { value: "8", label: "Agosto" },    { value: "9", label: "Setembro" },
  { value: "10", label: "Outubro" },  { value: "11", label: "Novembro" }, { value: "12", label: "Dezembro" },
];

export default function AdminEventos() {
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState("");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtros — persistidos no localStorage para não perder a seleção entre sessões.
  const [ufFilter, setUfFilter] = useState(() => {
    try { return localStorage.getItem(STATE_FILTER_KEY) || "todos"; } catch { return "todos"; }
  });
  const [monthFilter, setMonthFilter] = useState(() => {
    try { return localStorage.getItem(MONTH_FILTER_KEY) || "todos"; } catch { return "todos"; }
  });
  const [yearFilter, setYearFilter] = useState(() => {
    try { return localStorage.getItem(YEAR_FILTER_KEY) || "todos"; } catch { return "todos"; }
  });
  useEffect(() => { try { localStorage.setItem(STATE_FILTER_KEY, ufFilter); } catch { /* ignore */ } }, [ufFilter]);
  useEffect(() => { try { localStorage.setItem(MONTH_FILTER_KEY, monthFilter); } catch { /* ignore */ } }, [monthFilter]);
  useEffect(() => { try { localStorage.setItem(YEAR_FILTER_KEY, yearFilter); } catch { /* ignore */ } }, [yearFilter]);

  useEffect(() => { api.get("/courses").then(r => setCourses(r.data || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api.get(`/attendance/course/${selected}`)
      .then(r => setRecords(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selected]);

  // UFs disponíveis = só os estados que têm curso cadastrado (mantém a lista enxuta)
  const availableUFs = useMemo(() => {
    const set = new Set(courses.map((c) => (c.estado || "").toUpperCase()).filter(Boolean));
    return UFS.filter((u) => set.has(u.value));
  }, [courses]);

  // Anos disponíveis (derivados das datas dos cursos)
  const availableYears = useMemo(() => {
    const set = new Set(
      courses.map(c => (c.start_date || "").slice(0, 4)).filter(y => /^\d{4}$/.test(y))
    );
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [courses]);

  // Cursos filtrados por UF + mês + ano — mesma regra do Leitor QR
  const filteredCourses = useMemo(() => {
    return courses.filter(c => {
      if (ufFilter !== "todos" && (c.estado || "").toUpperCase() !== ufFilter) return false;
      const sd = c.start_date || "";
      if (yearFilter !== "todos" && sd.slice(0, 4) !== yearFilter) return false;
      if (monthFilter !== "todos") {
        const mm = sd.slice(5, 7);
        const numMM = String(parseInt(mm || "0", 10));
        if (numMM !== monthFilter) return false;
      }
      return true;
    });
  }, [courses, ufFilter, monthFilter, yearFilter]);

  // Se os filtros deixam o curso selecionado fora da lista, limpa a seleção
  useEffect(() => {
    if (selected && !filteredCourses.find((c) => c.id === selected)) {
      setSelected("");
    }
  }, [filteredCourses, selected]);

  const issueAll = async () => {
    if (!selected) return;
    let count = 0;
    for (const r of records) {
      if (r.status === "presenca_concluida") {
        try { await api.post("/admin/certificates/issue", { user_id: r.user_id, course_id: selected }); count++; } catch { /* ignore */ }
      }
    }
    toast.success(`${count} certificado(s) emitido(s)`);
  };

  const reload = () => {
    if (!selected) return;
    api.get(`/attendance/course/${selected}`).then(r => setRecords(r.data || [])).catch(() => {});
  };

  return (
    <AdminLayout title="Eventos / Presença">
      {/* Barra de filtros — mesmo padrão do Leitor QR */}
      <Card className="rounded-xl mb-3">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* UF */}
            <div>
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" /> Estado
              </p>
              <Select value={ufFilter} onValueChange={setUfFilter}>
                <SelectTrigger data-testid="eventos-uf-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os estados</SelectItem>
                  {availableUFs.map(u => (
                    <SelectItem key={u.value} value={u.value}>{u.value} — {u.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Mês */}
            <div>
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" /> Mês
              </p>
              <Select value={monthFilter} onValueChange={setMonthFilter}>
                <SelectTrigger data-testid="eventos-month-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os meses</SelectItem>
                  {MONTHS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Ano */}
            <div>
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" /> Ano
              </p>
              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger data-testid="eventos-year-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os anos</SelectItem>
                  {availableYears.map(y => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {filteredCourses.length} curso(s) correspondem aos filtros
          </p>
        </CardContent>
      </Card>

      {/* Seleção do curso + ações */}
      <Card className="rounded-xl"><CardContent className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <p className="text-xs text-muted-foreground mb-1">Selecione um curso</p>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger data-testid="eventos-course-select"><SelectValue placeholder="Escolha o curso" /></SelectTrigger>
              <SelectContent>
                {filteredCourses.length === 0 && (
                  <SelectItem value="__none__" disabled>Nenhum curso para os filtros atuais</SelectItem>
                )}
                {filteredCourses.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.title} — {c.cidade}/{c.estado}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={reload} disabled={!selected} data-testid="events-reload">
            <RefreshCw className="w-4 h-4 mr-1" /> Recarregar
          </Button>
          <Link to="/admin/leitor-qr">
            <Button data-testid="events-go-scanner"><ScanLine className="w-4 h-4 mr-1" /> Ler QR</Button>
          </Link>
          <Button onClick={issueAll} disabled={!selected || records.length === 0} data-testid="events-issue-all">
            <Award className="w-4 h-4 mr-1" /> Liberar certificados
          </Button>
          <Button variant="outline" disabled={!selected} onClick={async () => {
            try {
              const token = localStorage.getItem("ceap_token");
              const res = await fetch(`${API}/admin/reports/attendance/${selected}/csv`, { headers: { Authorization: `Bearer ${token}` } });
              if (!res.ok) throw new Error("falha");
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a"); a.href = url; a.download = "presenca.csv";
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
              setTimeout(() => URL.revokeObjectURL(url), 30000);
              toast.success("CSV exportado");
            } catch { toast.error("Erro ao exportar"); }
          }} data-testid="events-export-csv">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Exportar CSV
          </Button>
        </div>
      </CardContent></Card>

      <Card className="rounded-xl mt-4"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Aluno</TableHead><TableHead>CEAP ID</TableHead><TableHead>Órgão</TableHead>
            <TableHead>Check-in</TableHead><TableHead>Check-out</TableHead><TableHead>Horas</TableHead><TableHead>Frequência</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">Carregando…</TableCell></TableRow>}
            {!loading && records.map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.user?.full_name}</TableCell>
                <TableCell className="font-mono-id text-xs">{r.user?.ceap_id}</TableCell>
                <TableCell className="text-xs">{r.user?.orgao}</TableCell>
                <TableCell className="text-xs">{r.checkin_at ? new Date(r.checkin_at).toLocaleString("pt-BR") : "—"}</TableCell>
                <TableCell className="text-xs">{r.checkout_at ? new Date(r.checkout_at).toLocaleString("pt-BR") : "—"}</TableCell>
                <TableCell>{r.hours_attended || 0}h</TableCell>
                <TableCell>{r.attendance_percent || 0}%</TableCell>
                <TableCell>
                  {r.status === "presenca_concluida" && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Concluída</Badge>}
                  {r.status === "presenca_parcial" && <Badge className="bg-amber-100 text-amber-800 border-amber-200">Parcial</Badge>}
                  {r.status === "saida_pendente" && <Badge className="bg-blue-100 text-blue-800 border-blue-200">Saída pendente</Badge>}
                  {r.status === "entrada_registrada" && <Badge className="bg-blue-100 text-blue-800 border-blue-200">Entrada</Badge>}
                  {r.status === "ausente" && <Badge variant="outline">Ausente</Badge>}
                </TableCell>
              </TableRow>
            ))}
            {!loading && records.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">
                  {selected ? "Nenhuma presença registrada" : "Selecione um curso"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent></Card>
    </AdminLayout>
  );
}
